import { linkTo } from "../api/links";
import { useBatchOutcomes } from "../api/queries";
import type { Batch } from "../api/types";
import {
  Badge,
  Empty,
  Field,
  FieldGrid,
  Guid,
  OutcomeSummary,
  StalePending,
  StatusBadge,
  Time,
} from "./primitives";

type Column = "root" | "driver" | "device";

/**
 * Batches as a table, not as wrapped label/value pairs: guids are fixed-width, so putting
 * them in aligned columns is what makes a screenful of them scannable.
 *
 * A column disappears when it would repeat the same value down every row — the screen you are
 * already on, or a value the whole list shares. It is stated once above the table instead.
 */
export function BatchTable({
  batches,
  omit = [],
  empty,
}: {
  batches: Batch[];
  omit?: Column[];
  empty: React.ReactNode;
}) {
  const outcomes = useBatchOutcomes(batches.map((batch) => batch.batch_guid));

  if (batches.length === 0) return <Empty>{empty}</Empty>;

  const constant = (column: Column, pick: (batch: Batch) => string) =>
    omit.includes(column) || new Set(batches.map(pick)).size === 1;

  const hidden = {
    root: constant("root", (batch) => batch.root_guid),
    driver: constant("driver", (batch) => batch.driver_guid),
    device: constant("device", (batch) => batch.device_guid),
  };
  const shared: Array<[string, string, string | undefined]> = [];
  if (hidden.root && !omit.includes("root")) {
    shared.push([batches[0]!.root_type, batches[0]!.root_guid, linkTo.load(batches[0]!.root_guid)]);
  }
  if (hidden.driver && !omit.includes("driver")) {
    shared.push(["driver", batches[0]!.driver_guid, linkTo.driver(batches[0]!.driver_guid)]);
  }
  if (hidden.device && !omit.includes("device")) {
    shared.push(["device", batches[0]!.device_guid, linkTo.device(batches[0]!.device_guid)]);
  }

  const template = [
    "76px",
    "minmax(140px, auto)",
    "minmax(300px, auto)",
    "64px",
    !hidden.root && "minmax(300px, auto)",
    !hidden.driver && "minmax(300px, auto)",
    !hidden.device && "minmax(300px, auto)",
    "minmax(120px, 1fr)",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      {shared.length > 0 && (
        <FieldGrid className="border-b border-line bg-raised">
          <div className="col-span-full">
            <span
              className="label"
              title={
                "These columns are dropped because every batch below has the same value. They " +
                "come back per row as soon as the values differ — a load reassigned to another " +
                "driver, or a driver flushing from a second device."
              }
            >
              same on all {batches.length} {batches.length === 1 ? "row" : "rows"}
            </span>
          </div>
          {shared.map(([label, guid, to]) => (
            <Field key={label} label={label}>
              <Guid value={guid} to={to} />
            </Field>
          ))}
        </FieldGrid>
      )}

      <div className="overflow-x-auto">
        <div className="min-w-max">
          <div
            className="grid items-center gap-x-4 border-b border-line px-4 py-1.5"
            style={{ gridTemplateColumns: template }}
          >
            <span className="label">status</span>
            <span
              className="label"
              title="What the apply decided for this batch's events, from the journal. The status beside it is only whether the batch has drained."
            >
              journal
            </span>
            <span className="label">batch</span>
            <span className="label">events</span>
            {!hidden.root && <span className="label">load</span>}
            {!hidden.driver && <span className="label">driver</span>}
            {!hidden.device && <span className="label">device</span>}
            <span className="label text-right">received</span>
          </div>

          {batches.map((batch) => (
            <div
              key={batch.batch_guid}
              className="row-hover grid items-center gap-x-4 border-b border-line/70 px-4 py-2 last:border-b-0"
              style={{ gridTemplateColumns: template }}
            >
              <StatusBadge status={batch.status} counts={outcomes(batch.batch_guid).counts} />
              <OutcomeSummary
                {...outcomes(batch.batch_guid)}
                pending={batch.status === "pending"}
              />
              <Guid value={batch.batch_guid} to={linkTo.batch(batch.batch_guid)} />
              <span className="mono text-xs text-muted">{batch.events.length}</span>
              {!hidden.root && (
                <Guid value={batch.root_guid} to={linkTo.root(batch.root_type, batch.root_guid)} />
              )}
              {!hidden.driver && (
                <Guid value={batch.driver_guid} to={linkTo.driver(batch.driver_guid)} />
              )}
              {!hidden.device && (
                <Guid value={batch.device_guid} to={linkTo.device(batch.device_guid)} />
              )}
              <span className="flex items-center justify-end gap-2">
                {batch.attempts > 0 && <Badge tone="warn">{batch.attempts} attempts</Badge>}
                <StalePending batch={batch} />
                {batch.connection_status !== "activated" && (
                  <Badge tone="warn">{batch.connection_status ?? "no connection"}</Badge>
                )}
                <Time value={batch.received_at} />
              </span>

              {batch.last_error && (
                <p
                  className="mono col-span-full truncate pt-1 text-bad/90"
                  title={batch.last_error}
                >
                  {batch.last_error}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
